import { useTranslation } from "react-i18next"

import type { FairUseThresholdId } from "./fair-use-settings-utils"
import type { FairUseDowngradeModelOption, FairUseReasoningEffort } from "@/api/admin/fair-use"

import { RefreshCw } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"

import { FairUseDowngradeFields } from "./fair-use-downgrade-fields"
import { FairUseLeadAllowanceField } from "./fair-use-lead-allowance-field"
import {
  fairUseThresholdFormField,
  fairUseThresholdIssue,
  parseFairUseLeadAllowance,
  readFairUseThresholdValues,
} from "./fair-use-settings-utils"
import { FairUseThresholdLadder } from "./fair-use-threshold-ladder"
import { type FairUseConfigFormState } from "./fair-use-utils"

type FairUseLimitsPanelProps = {
  form: FairUseConfigFormState
  modelOptions: FairUseDowngradeModelOption[]
  isModelResolvedByPlatform: boolean
  /**
   * Whether the backend carries `lead_allowance_sek_per_month` at all. False on
   * a round-1 deployment, where sending the field would answer 400
   * `invalid_config` and take the four thresholds down with it.
   */
  showLeadAllowance: boolean
  isSaving: boolean
  isDirty: boolean
  onChange: (patch: Partial<FairUseConfigFormState>) => void
  onSave: () => void
}

/**
 * What happens as a user spends — the delegable half of fair use.
 *
 * Everything here is something a customer admin with the `fair_use`
 * capability may change: whether fair use acts at all, the four steps it acts
 * at, and what a fallback turn runs as. Nothing on this panel is an input to
 * an invoice, which is why its Save sends the delegable fields and only those
 * ({@link fairUseLimitsUpdate}); a commercial field in the same payload would
 * answer 403 and apply nothing, including the step the admin actually came
 * here to move.
 */
export function FairUseLimitsPanel({
  form,
  modelOptions,
  isModelResolvedByPlatform,
  showLeadAllowance,
  isSaving,
  isDirty,
  onChange,
  onSave,
}: FairUseLimitsPanelProps) {
  const { t } = useTranslation()

  const thresholds = readFairUseThresholdValues(form)
  const issue = fairUseThresholdIssue(thresholds)
  const leadAllowanceIssue =
    showLeadAllowance && parseFairUseLeadAllowance(form.leadAllowanceSek) == null
  const canSave =
    isDirty &&
    issue === null &&
    !leadAllowanceIssue &&
    form.downgradeModel.trim() !== "" &&
    !isSaving

  const handleThresholdChange = (id: FairUseThresholdId, value: string) => {
    onChange({ [fairUseThresholdFormField(id)]: value } as Partial<FairUseConfigFormState>)
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-muted-foreground max-w-prose text-sm">
          {t(
            "adminDashboard.fairUse.settings.limits.caption",
            "Applies to every user on every plan. Percentages are of the user's monthly budget."
          )}
        </p>
        <Button disabled={!canSave} onClick={onSave}>
          {isSaving ? <RefreshCw className="mr-2 size-4 animate-spin" /> : null}
          {t("adminDashboard.fairUse.settings.save", "Save")}
        </Button>
      </div>

      <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
        <div className="space-y-0.5">
          <Label className="text-sm font-semibold" htmlFor="fair-use-enabled">
            {t("adminDashboard.fairUse.settings.limits.enabledLabel", "Fair use is on")}
          </Label>
          <p className="text-muted-foreground text-xs">
            {t(
              "adminDashboard.fairUse.settings.limits.enabledHelp",
              "Turn off to keep tracking usage without warnings, fallback or stops."
            )}
          </p>
        </div>
        <Switch
          checked={form.enabled}
          id="fair-use-enabled"
          onCheckedChange={(checked) => onChange({ enabled: checked })}
        />
      </div>

      <FairUseThresholdLadder issue={issue} values={thresholds} onChange={handleThresholdChange} />

      <div className="grid gap-4 md:grid-cols-2">
        {/* `modelOptions` comes straight off the config RESPONSE, never off the
            form: it is derived platform data, outside the config document, its
            value hash and the PUT payload. */}
        <FairUseDowngradeFields
          effort={form.downgradeReasoningEffort}
          fallbackPercent={form.downgradePercent}
          isModelResolvedByPlatform={isModelResolvedByPlatform}
          model={form.downgradeModel}
          modelOptions={modelOptions}
          onModelChange={(downgradeModel) => onChange({ downgradeModel })}
          onEffortChange={(downgradeReasoningEffort: FairUseReasoningEffort) =>
            onChange({ downgradeReasoningEffort })
          }
        />
      </div>

      {showLeadAllowance ? (
        <FairUseLeadAllowanceField
          className="border-t pt-5"
          value={form.leadAllowanceSek}
          onChange={(leadAllowanceSek) => onChange({ leadAllowanceSek })}
        />
      ) : null}
    </div>
  )
}
